import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { FileText, Receipt, CreditCard, ClipboardList, Car } from 'lucide-react';

export const SupportedDocuments = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const documentTypes = [
    {
      icon: FileText,
      name: 'Invoice',
      description: 'Extract vendor, line items, totals, and tax information.',
    },
    {
      icon: Receipt,
      name: 'Receipt',
      description: 'Parse merchant details, items, payment methods, and amounts.',
    },
    {
      icon: CreditCard,
      name: 'ID Card',
      description: 'Extract personal details, ID numbers, and expiration dates.',
    },
    {
      icon: ClipboardList,
      name: 'Purchase Order',
      description: 'Parse order details, quantities, pricing, and shipping info.',
    },
    {
      icon: Car,
      name: 'Driving License',
      description: 'Extract license number, class, restrictions, and validity.',
    },
  ];

  return (
    <section
      ref={ref}
      className="py-20 md:py-28 bg-[#F7F8FC]"
      data-testid="supported-docs-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="inline-block px-4 py-1.5 bg-[#635BFF]/10 text-[#635BFF] text-sm font-medium rounded-full mb-4">
            Supported Documents
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-[#0A2540] mb-6">
            Process Any Document Type
          </h2>
          <p className="text-lg text-[#425466] leading-relaxed">
            Our AI understands the structure of common business documents and extracts data with high accuracy.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-6">
          {documentTypes.map((doc, index) => (
            <motion.div
              key={doc.name}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group bg-white rounded-2xl p-6 text-center border border-gray-100 hover:border-[#635BFF] hover:shadow-lg transition-all duration-300 cursor-pointer"
              data-testid={`doc-type-${doc.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="w-16 h-16 bg-[#F7F8FC] group-hover:bg-[#635BFF]/10 rounded-2xl flex items-center justify-center mx-auto mb-4 transition-colors duration-300">
                <doc.icon className="w-8 h-8 text-[#425466] group-hover:text-[#635BFF] transition-colors duration-300" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-[#0A2540] mb-2">
                {doc.name}
              </h3>
              <p className="text-sm text-[#969DA3] leading-relaxed">
                {doc.description}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="mt-12 text-center"
        >
          <p className="text-[#425466]">
            <span className="font-medium text-[#0A2540]">And more...</span> Custom document types can be configured via the API.
          </p>
        </motion.div>
      </div>
    </section>
  );
};
